import Autocomplete from "@mui/material/Autocomplete";
import React from "react";

import { InputAdornment } from "@/Components/v2/atoms/InputAdornment";
import { Stack } from "@/Components/v2/atoms/Stack";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";

import { OptionV2, SingleSelectProps } from "./types";

const DefaultWidth = 240;

export const SingleSelectV2 = ({
  label,
  id,
  options,
  value,
  onChange,
  width = DefaultWidth,
  disabled = false,
  disableClearable = false,
  placeholderOverride,
  multiple = false,
  sx,
}: SingleSelectProps) => {
  const selectedOption = options.find((opt) => opt.value === value);

  const handleChange = (_: unknown, selected: OptionV2 | null) => {
    onChange(selected?.value ?? null);
  };

  const getSelectedLabel = () => {
    if (!value) return undefined;
    return selectedOption?.label;
  };

  return (
    <Autocomplete
      id={id}
      options={options}
      value={selectedOption ?? null}
      onChange={handleChange}
      disabled={disabled}
      disableClearable={disableClearable}
      multiple={multiple}
      getOptionLabel={(option: OptionV2) => option.label}
      sx={{
        width,
        "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
          paddingLeft: "12px",
          borderRadius: "4px",
        },
        "& .MuiInputLabel-root": {
          fontSize: "14px",
        },
        "& .MuiAutocomplete-input": {
          fontSize: "14px",
        },
        "& .MuiInputBase-input": {
          fontSize: "14px",
        },
        ...sx,
      }}
      isOptionEqualToValue={(option: OptionV2, value: OptionV2) => option.value === value.value}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: OptionV2) => (
        <li {...props} key={option.value}>
          {option.label}
        </li>
      )}
      style={{ width }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={getSelectedLabel() || placeholderOverride || `Select a ${label}`}
          id={id}
          sx={{
            "& .MuiInputBase-root": {
              padding: "4px 8px",
              borderRadius: "8px",
            },
            "& .MuiInputLabel-root": {
              display: "none",
            },
          }}
          InputLabelProps={{}}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {label && (
                    <InputAdornment position="start" sx={{ marginRight: "4px" }}>
                      <Typography fontSize="16px" fontWeight="600" color="text.secondary">
                        {label}:
                      </Typography>
                    </InputAdornment>
                  )}
                </Stack>
              </>
            ),
          }}
          size="small"
        />
      )}
    />
  );
};
